import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      toast.success("Password updated — please sign in with your new password.");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div data-testid="reset-password-no-token" className="max-w-[560px] mx-auto px-6 py-24 text-center">
        <XCircle size={44} className="mx-auto text-red-500" />
        <h1 className="font-display text-4xl tracking-tighter mt-5">Link incomplete.</h1>
        <p className="mt-4 text-neutral-500">
          This page needs a reset link from your email. Request a fresh one and try again.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/forgot-password" className="btn-primary" data-testid="reset-request-new">Request a new link</Link>
          <Link to="/auth" className="btn-outline">Back to sign in</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div data-testid="reset-password-success" className="max-w-[560px] mx-auto px-6 py-24 text-center">
        <CheckCircle2 size={44} className="mx-auto" style={{ color: "#1E7A3B" }} />
        <h1 className="font-display text-4xl tracking-tighter mt-5">Password updated.</h1>
        <p className="mt-4 text-neutral-500">
          For your security we've signed you out everywhere else. Sign in with your new password.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <button className="btn-primary" data-testid="reset-goto-signin" onClick={() => nav("/auth")}>Sign in</button>
          <Link to="/" className="btn-outline">Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="max-w-[520px] mx-auto px-6 py-16">
      <div className="label-eyebrow">Account recovery</div>
      <h1 className="font-display text-5xl tracking-tighter mt-3">Set a new password.</h1>
      <p className="mt-4 text-neutral-500">Choose something you haven't used here before — at least 6 characters.</p>

      <form onSubmit={submit} className="hairline mt-6 p-8 space-y-4">
        <div>
          <label className="label-eyebrow">New password</label>
          <input required type="password" minLength={6} data-testid="reset-input-password"
            className="w-full mt-2 hairline p-3 text-sm bg-white outline-none focus:border-black"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div>
          <label className="label-eyebrow">Confirm new password</label>
          <input required type="password" minLength={6} data-testid="reset-input-confirm"
            className="w-full mt-2 hairline p-3 text-sm bg-white outline-none focus:border-black"
            value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
        </div>
        {error && <div data-testid="reset-error" className="text-sm text-red-600">{error}</div>}
        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit">
          {loading ? "…" : "Update password"}
        </button>
      </form>

      <p className="mt-4 text-xs text-neutral-500">
        Link stopped working?{" "}
        <Link className="underline" to="/forgot-password" data-testid="reset-new-link">Request a new one</Link>.
      </p>
    </div>
  );
}
